'use client';

import { useState } from 'react';
import { Mail, Phone, Check, ArrowRight, Target, Lightbulb, Heart } from 'lucide-react';
import { AnimatedPage } from '@/components/AnimatedPage';
import { EmailLink } from '@/components/EmailLink';

interface ContactFormProps {
  phoneNumber?: string;
}

export function ContactForm({ phoneNumber }: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    organizationType: '',
    subject: '',
    message: '',
    website: '', // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          subject: `[Zakelijk] ${formData.subject}`,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setIsSubmitted(true);
      } else {
        setError(data.message || 'Er is iets misgegaan. Probeer het later opnieuw.');
      }
    } catch {
      setError('Er is iets misgegaan. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Format phone number for display
  // Mobile: +31636502567 -> 06 - 3650 2567
  // Landline: +31851234567 -> 085 - 123 4567
  const formatPhone = (phone: string) => {
    const withZero = phone.replace(/^\+31/, '0');
    // Mobile numbers (06): 06 - xxxx xxxx
    if (withZero.startsWith('06')) {
      return withZero.replace(/(\d{2})(\d{4})(\d{4})/, '$1 - $2 $3');
    }
    // Landline numbers (0xx): 0xx - xxx xxxx
    return withZero.replace(/(\d{3})(\d{3})(\d{4})/, '$1 - $2 $3');
  };

  return (
    <AnimatedPage className="bg-white">
      {/* Hero with Form */}
      <section className="pt-28 pb-8 md:pb-10 lg:pb-12">
        <div className="mx-4 md:mx-6 lg:mx-8 bg-gradient-to-br from-cta via-cta-dark to-ink rounded-3xl py-16 md:py-20 relative overflow-hidden">
          {/* Geometric shapes */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 border-2 border-white/10 rotate-45 -translate-x-1/2 translate-y-1/2" />

          <div className="container-site relative">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
              {/* Left - Info */}
              <div className="animate-hero">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal text-white mb-8">
                  Waarom <span className="font-handwriting text-yellow-400">contact</span> opnemen?
                </h1>

                {/* Benefits */}
                <div className="space-y-6 mb-10">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white text-lg">Persoonlijke aanpak</p>
                      <p className="text-white/70">Geen verkooppraatje, maar een eerlijk gesprek over wat werkt voor uw situatie.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white text-lg">Advies op maat</p>
                      <p className="text-white/70">We kijken naar uw doelgroep, budget en wensen om de beste oplossing te vinden.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-white text-lg">Geen verplichtingen</p>
                      <p className="text-white/70">Een verkennend gesprek is altijd vrijblijvend. We delen graag onze kennis.</p>
                    </div>
                  </div>
                </div>

                {/* Contact info */}
                <div className="flex flex-wrap gap-6">
                  <EmailLink name="service" className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                    <Mail className="w-5 h-5" />
                    service@valbewust.nl
                  </EmailLink>
                  {phoneNumber && (
                    <a href={`tel:${phoneNumber}`} className="flex items-center gap-3 text-white/80 hover:text-white transition-colors">
                      <Phone className="w-5 h-5" />
                      {formatPhone(phoneNumber)}
                    </a>
                  )}
                </div>
              </div>

              {/* Right - Form */}
              <div className="bg-white rounded-[30px] p-8 md:p-10 shadow-xl animate-on-scroll">
                {isSubmitted ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-cta/10 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Check className="w-10 h-10 text-cta" />
                    </div>
                    <h2 className="text-2xl font-bold text-ink mb-3">Bedankt voor uw bericht!</h2>
                    <p className="text-lg text-ink/70 mb-6">We nemen binnen 1 werkdag contact met u op.</p>
                    <a href="/" className="inline-flex items-center text-cta font-semibold hover:underline">
                      Terug naar homepage
                      <ArrowRight size={18} className="ml-2" />
                    </a>
                  </div>
                ) : (
                  <>
                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                        {error}
                      </div>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-5 relative">
                      {/* Honeypot field */}
                      <div className="absolute -left-[9999px]" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                          type="text"
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-ink mb-2">
                            Uw naam *
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                          />
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-ink mb-2">
                            E-mailadres *
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-5">
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-ink mb-2">
                            Telefoonnummer
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                          />
                        </div>

                        <div>
                          <label htmlFor="organization" className="block text-sm font-medium text-ink mb-2">
                            Organisatie *
                          </label>
                          <input
                            type="text"
                            id="organization"
                            name="organization"
                            required
                            value={formData.organization}
                            onChange={handleChange}
                            className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="organizationType" className="block text-sm font-medium text-ink mb-2">
                          Type organisatie *
                        </label>
                        <select
                          id="organizationType"
                          name="organizationType"
                          required
                          value={formData.organizationType}
                          onChange={handleChange}
                          className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                        >
                          <option value="">Selecteer type organisatie</option>
                          <option value="gemeente">Gemeente</option>
                          <option value="zorgverzekeraar">Zorgverzekeraar</option>
                          <option value="wmo-aanbieder">WMO-aanbieder</option>
                          <option value="zorginstelling">Zorginstelling</option>
                          <option value="anders">Anders</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-ink mb-2">
                          Onderwerp *
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          required
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors"
                        >
                          <option value="">Selecteer een onderwerp</option>
                          <option value="Pilot starten">Pilot starten</option>
                          <option value="Demo aanvragen">Demo aanvragen</option>
                          <option value="Offerte aanvragen">Offerte aanvragen</option>
                          <option value="Algemene vraag">Algemene vraag</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-ink mb-2">
                          Uw bericht *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          rows={4}
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Vertel ons over uw organisatie en wat u wilt bereiken..."
                          className="w-full px-4 py-3 text-ink bg-white rounded-xl border-2 border-ink/10 focus:border-cta focus:ring-2 focus:ring-cta/20 outline-none transition-colors resize-none"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 px-6 bg-ink text-white rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 hover:bg-cta disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                      >
                        {isSubmitting ? 'Verzenden...' : 'Verstuur bericht'}
                      </button>

                      <p className="text-sm text-ink/50 text-center">
                        Door te verzenden gaat u akkoord met onze{' '}
                        <a href="/privacy" className="underline hover:text-cta">Privacyverklaring</a>.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </AnimatedPage>
  );
}
